/* ----------------- React Native Paper --------------- */
import { List, Text } from "react-native-paper";

/* ----------------- Memorang UI --------------- */
import {
	Box,
	CardContainer,
	CustomChip,
	DonutChart,
	type IconType,
	useDevice,
} from "@memorang/ui";

/* ----------------- Memorang Types --------------- */
import type { Distribution, Item, MixedItemTag } from "@memorang/types";

import { skillIconMap } from "@constants/common";
import { isMockedData, mockedTrademark } from "@constants/demo";
import { events } from "@constants/tracking";
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import {
	getCompositeScoreAndLevel,
	skillCutoffs,
} from "@features/report/helpers/points-normalization";
import type { Skill } from "@features/report/helpers/points-normalization";
import {
	calculateNumAttempts,
	sortedMasteryLevels,
} from "@helpers/content/distribution";
import { isWeb } from "@helpers/platform";
import { smartPluralize } from "@helpers/smartPluralize";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
/* ----------------- Local Components --------------- */
import { ScrollContainer } from "@memorang/ui";
import { trackCustomEvent } from "analytics";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import InfoAlert from "components/InfoAlert";
import { CustomAppbar } from "components/index";
import { Link } from "expo-router";
import { useTranslation } from "react-i18next";
import type { StyleProp, ViewStyle } from "react-native";

type Props = {
	title: string;
	tags: MixedItemTag[];
};

const getSkillKnowMoreUrl = (skill: string) => {
	return `https://www.ets.org/toefl/test-takers/ibt/about/content/${skill.toLowerCase()}.html`;
};

type GroupedItems = {
	[variant: string]: MixedItemTag;
};

const ProficiencyChildListItem = ({
	distKey,
	title,
	icon,
	iconColor,
	description,
	count,
}: {
	distKey: string;
	title: string;
	icon: IconType;
	iconColor: string;
	description: string;
	count: number;
}) => {
	const theme = useAppTheme();
	return (
		<List.Item
			key={distKey}
			titleStyle={{
				fontSize: 14,
			}}
			title={title}
			left={(p) => <List.Icon {...p} icon={icon} color={iconColor} />}
			descriptionNumberOfLines={3}
			description={<CustomMarkdownRenderer fontSize={12} text={description} />}
			descriptionStyle={{
				color: theme.colors.onSurfaceVariant,
				fontSize: 12,
			}}
			style={{
				paddingLeft: 54,
			}}
			right={(p) => (
				<List.Icon
					{...p}
					icon={() => (
						<CustomChip>
							{`${count} ${smartPluralize("item", count)}`}
						</CustomChip>
					)}
				/>
			)}
		/>
	);
};

export const TopicDetailView = ({ title, tags }: Props) => {
	const { t } = useTranslation();

	const theme = useAppTheme();

	const { isMobile } = useDevice();

	const numItemsAttempted = tags.reduce(
		(acc, tag) => acc + calculateNumAttempts(tag.distribution),
		0,
	);

	const mappedTags = tags.map((tag) => {
		const groupedByVariant = tag.items?.reduce(
			(acc: GroupedItems, item: Item) => {
				if (!item.variant) {
					return acc;
				}
				if (!acc[item.variant]) {
					acc[item.variant] = {
						type: item.variant,
						title: t(`session.itemTypes.${item.variant}`),
						distribution: sortedMasteryLevels
							.map((level) => level.id)
							.reduce<Record<string, number>>((acc, level) => {
								acc[level] = 0;
								return acc;
							}, {}),
						items: [],
						id: `${item.variant}-${item.id}`,
					};
				}
				acc[item.variant]!.items?.push(item);

				return acc;
			},
			{},
		);
		const score = Math.round(tag.averageScore || 0);

		return {
			...tag,
			title: t(`dashboard.skills.${tag.label || tag.title}`) || "",
			distribution: tag.distribution,
			score,
			childTags: groupedByVariant ? Object.values(groupedByVariant) : [],
		};
	});

	const renderLeftComp = ({
		p,
		distribution,
		innerRadius = 12,
		outerRadius = 22,
		score,
		icon,
		masteryLevel,
	}: {
		p: {
			color: string;
			style: StyleProp<ViewStyle>;
		};
		distribution: Distribution;
		innerRadius?: number;
		outerRadius?: number;
		score?: number;
		icon?: IconType;
		masteryLevel?: (typeof sortedMasteryLevels)[number];
	}) => {
		const colorScale = [
			masteryLevel?.lightColor || theme.colors.primary,
			theme.colors.progress.none,
		];
		return (
			<Box {...p} justifyContent="center">
				<DonutChart
					distribution={score != null ? [score, 30 - score] : distribution}
					innerRadius={innerRadius}
					radius={outerRadius}
					colorScale={colorScale}
					score={score}
					variant={score != null ? "progress" : "mastery"}
				>
					{icon && (
						<Icon name={icon} size={18} color={theme.colors.onSurfaceVariant} />
					)}
				</DonutChart>
			</Box>
		);
	};

	const ListSection = (propsListSection: MixedItemTag) => {
		const { title, distribution, childTags } = propsListSection;

		const hasChildTags = childTags && childTags.length > 0;

		const trademark = useAppStore((state) => state.app.trademark);

		const finalTrademark = isMockedData ? mockedTrademark : trademark;
		const numAttempts = calculateNumAttempts(distribution);
		const numItems = Object.keys(distribution).reduce(
			(acc, key) => acc + distribution[key as keyof Distribution],
			0,
		);

		const numNotAttempted = propsListSection.distribution.none;
		const distKeys = Object.keys(distribution)
			.filter((key) => key !== "none")
			.reverse();
		const finalDistKeys = [...distKeys, "none"];

		const proficiency = getCompositeScoreAndLevel(
			[
				{
					skill: propsListSection.label as Skill,
					numPointsEarned: propsListSection.numPointsEarned || 0,
					numTotalPoints: propsListSection.numTotalPoints || 0,
				},
			],
			t,
			theme,
		);
		const masteryLevel = sortedMasteryLevels.find(
			(item) => item.id === proficiency.compositeLevel,
		);
		const description = `${t("common.estimatedProficiency")} - ${t(masteryLevel?.alias.title || "")}`;
		const descriptionAttempted = `${numAttempts} ${t("common.of")} ${numItems} ${smartPluralize("item", numItems)} ${t("common.attempted")}`;

		if (hasChildTags) {
			return (
				<List.Accordion
					title={title}
					descriptionNumberOfLines={3}
					style={{
						backgroundColor: theme.colors.elevation.level1,
					}}
					description={
						isMobile ? (
							<Box paddingTop={4}>
								<Text variant="bodySmall">{description}</Text>
								<Text
									style={{
										color: theme.colors.onSurfaceVariant,
										lineHeight: 20,
									}}
									variant="labelSmall"
								>
									{descriptionAttempted}
								</Text>
							</Box>
						) : (
							description
						)
					}
					titleStyle={{
						fontSize: 14,
					}}
					descriptionStyle={{
						color: theme.colors.onSurfaceVariant,
						fontSize: 12,
					}}
					left={(p) =>
						renderLeftComp({
							p,
							distribution,
							score: proficiency.compositeScaledScore,
							innerRadius: 16,
							outerRadius: 20,
							icon: skillIconMap[title],
							masteryLevel,
						})
					}
					onPress={() => {
						trackCustomEvent({
							eventName: events.skillExpanded,
							skill: title,
						});
					}}
					titleNumberOfLines={3}
					right={({ isExpanded }) => (
						<Box flexDirection="row" gap={8} alignItems="center">
							{!isMobile && (
								<Text
									style={{
										color: theme.colors.onSurfaceVariant,
										lineHeight: 24,
									}}
									variant="labelSmall"
								>
									{descriptionAttempted}
								</Text>
							)}
							<List.Icon
								icon={isExpanded ? "chevron-up" : "chevron-down"}
								color={theme.colors.onSurfaceVariant}
							/>
						</Box>
					)}
				>
					{finalDistKeys.map((item) => {
						if (item === "none") {
							return (
								<ProficiencyChildListItem
									key={item}
									distKey={item}
									title={t("common.notAnswered")}
									description={t("common.didNotAnswer")}
									count={numNotAttempted}
									icon={"speedometer-slow" as const}
									iconColor={theme.colors.onSurfaceVariant}
								/>
							);
						}

						const masteryLevel = sortedMasteryLevels.find(
							(level) => level.id === item,
						);
						const skillCutoff =
							skillCutoffs[propsListSection.label!][
								item as keyof (typeof skillCutoffs)[string]
							];

						return (
							<ProficiencyChildListItem
								key={item}
								distKey={item}
								title={`${t(`session.masteryLevels.${item}`)} ${t("common.proficiency")}`}
								description={t("session.masteryLevels.proficiencySubtitle", {
									trademark: finalTrademark,
									proficiency: t(
										`session.masteryLevels.${item}`,
									)?.toLowerCase(),
									minScore: skillCutoff.min,
									maxScore: skillCutoff.max,
									skill: title.toLowerCase(),
								})}
								count={distribution[item]}
								icon={masteryLevel?.icon as IconType}
								iconColor={masteryLevel?.lightColor!}
							/>
						);
					})}

					<Box
						paddingTop={16}
						paddingBottom={8}
						borderBottomWidth={1}
						paddingLeft={8}
						paddingRight={8}
						borderBottomColor={theme.colors.divider}
					>
						<InfoAlert
							text={t(
								`session.masteryLevels.infoSectionText.${propsListSection.label}`,
								{
									trademark: finalTrademark,
								},
							)}
							fontSize={11}
							bottomComponent={
								isWeb && (
									<Link
										href={
											getSkillKnowMoreUrl(propsListSection.label || "") as never
										}
										style={{
											paddingTop: 16,
										}}
										target="_blank"
									>
										<Text
											variant="labelMedium"
											style={{
												color: theme.colors.primary,
											}}
										>
											{t("common.knowMore")}
										</Text>
									</Link>
								)
							}
							alignItems="flex-start"
							backgroundColor={theme.palette.info.main}
							title={t("session.masteryLevels.infoSectionLabel", {
								skill: title,
							})}
						/>
					</Box>
				</List.Accordion>
			);
		}
		return (
			<>
				<List.Item
					title={title}
					left={(p) => (
						<List.Icon
							{...p}
							icon="speedometer-low"
							color={theme.colors.onSurfaceVariant}
						/>
					)}
					right={(p) => (
						<List.Icon
							{...p}
							icon={() => <CustomChip>{numNotAttempted}</CustomChip>}
							color={theme.colors.onSurfaceVariant}
						/>
					)}
				/>
			</>
		);
	};

	const subtitle = `${t("progress.tagProgress.subtitle", {
		numItems: numItemsAttempted,
		type: smartPluralize("skill", tags.length),
		count: tags.length,
		item: smartPluralize("item", numItemsAttempted),
	})}`;

	return (
		<>
			<CustomAppbar
				options={{
					headerTitle: `${t("common.performance")} ${t("common.details")}`,
					headerRight: () => null,
				}}
				back
				mode={isMobile ? "small" : "medium"}
			/>
			<ScrollContainer
				maxWidth="md"
				contentContainerStyle={{
					paddingBottom: 100,
				}}
			>
				{isMobile ? (
					<CardContainer
						title={title}
						subtitle={subtitle}
						icon={"tag-multiple-outline"}
					>
						{mappedTags.map((item) => (
							<ListSection key={item.id} {...item} />
						))}
					</CardContainer>
				) : (
					<Box padding={24}>
						<CardContainer
							title={title}
							subtitle={subtitle}
							icon={"tag-multiple-outline"}
						>
							<Box>
								{mappedTags.map((item) => (
									<ListSection key={item.id} {...item} />
								))}
							</Box>
						</CardContainer>
					</Box>
				)}
			</ScrollContainer>
		</>
	);
};
